import {FC, useMemo, useRef, useState} from 'react'
import {Spinner} from 'react-bootstrap'
import {useParams} from 'react-router-dom'
import {FIELD_MAPPING_TYPE, SETTINGS_OF_PLATFORM} from 'src/interfaces/Platform.interface'
import useAppToast from 'src/hooks/useAppToast'
import {useAppSelector} from 'src/redux/storeHooks'
import {KTIcon} from 'src/marketConnect/helpers'
import {
  downloadManageSettingFile,
  extractManageSettingFileErrorMessage,
  uploadManageSettingFile,
} from 'src/utils/manageSettingFile'
import {
  buildManageSettingFileParamsFromChildSetting,
  buildManageSettingFileParamsFromMapping,
  getSettingFileActionButtonLabel,
  getSettingFilePanelMeta,
  getSettingFileRowLabel,
  isSettingFileDownloadInputType,
  isSettingFileUploadInputType,
} from 'src/utils/settingFileFieldMappings'
import {getSanitizedSettingNotesHtml} from './SettingNotes'
import InfoTooltip from './InfoTooltip'
import {applyPlatformSettingShortcodes} from 'src/utils/platformSettingDisplayText'

type SettingFileActionCalloutProps = {
  mappings?: FIELD_MAPPING_TYPE[]
  childSettings?: SETTINGS_OF_PLATFORM[]
  panelTitle?: string
  panelDescription?: string
  platformId?: number | string | null
  parentAvailablePlatformSettingId?: number | null
  disabled?: boolean
  className?: string
  sourceChannelName?: string | null
}

const SettingFileActionCallout: FC<SettingFileActionCalloutProps> = ({
  mappings = [],
  childSettings = [],
  panelTitle,
  panelDescription,
  platformId,
  parentAvailablePlatformSettingId,
  disabled = false,
  className = '',
  sourceChannelName,
}) => {
  const {selectedPlatformId} = useParams()
  const effectivePlatformId = Number(platformId ?? selectedPlatformId)
  const {retrivedPlatformData} = useAppSelector((state) => state.retrivePlatformData)
  const {mainStoreData} = useAppSelector((state) => state.fetchConnectedPlatform)
  const formatSettingDisplayText = (text: string | null | undefined) =>
    applyPlatformSettingShortcodes(text, {
      retrievedPlatform: retrivedPlatformData,
      mainStore: mainStoreData,
      sourceChannelName,
    })
  const {successToast, errorToast} = useAppToast()
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const [activeUploadKey, setActiveUploadKey] = useState<string | null>(null)
  const [downloadingKey, setDownloadingKey] = useState<number | string | null>(null)
  const [uploadingKey, setUploadingKey] = useState<number | string | null>(null)

  const downloadMapping = useMemo(
    () => mappings.find((mapping) => isSettingFileDownloadInputType(mapping.input_type)) ?? null,
    [mappings]
  )
  const uploadMapping = useMemo(
    () => mappings.find((mapping) => isSettingFileUploadInputType(mapping.input_type)) ?? null,
    [mappings]
  )
  const downloadChild = useMemo(
    () => childSettings.find((child) => isSettingFileDownloadInputType(child.input_type)) ?? null,
    [childSettings]
  )
  const uploadChild = useMemo(
    () => childSettings.find((child) => isSettingFileUploadInputType(child.input_type)) ?? null,
    [childSettings]
  )

  const panelMeta = useMemo(
    () =>
      getSettingFilePanelMeta({
        mappings,
        childSettings,
        panelTitle,
        panelDescription,
      }),
    [mappings, childSettings, panelTitle, panelDescription]
  )

  const hasUploadAction = Boolean(uploadMapping || uploadChild)
  const iconName = hasUploadAction ? 'file' : 'laptop'
  const showPanelHeader = Boolean(panelMeta.title || panelMeta.description)

  const hasDownloadAction = Boolean(downloadMapping || downloadChild)
  const actionCount =
    Number(Boolean(downloadMapping)) +
    Number(Boolean(uploadMapping)) +
    Number(Boolean(downloadChild)) +
    Number(Boolean(uploadChild))
  const isSingleDownloadOnly = hasDownloadAction && !hasUploadAction && actionCount === 1

  const downloadItem = downloadMapping ?? downloadChild
  const uploadItem = uploadMapping ?? uploadChild
  const isInlineHeaderActions =
    showPanelHeader && Boolean(downloadItem) && Boolean(uploadItem)

  const getDownloadIconName = () => (downloadMapping ? 'file' : 'laptop')
  const getUploadIconName = () => 'file'

  const getButtonLabel = (
    item: {display_name?: string | null; placeholder?: string | null},
    isDownload: boolean
  ) =>
    getSettingFileActionButtonLabel({
      displayName: item.display_name,
      placeholder: item.placeholder,
      panelTitle: panelMeta.title,
      isDownload,
      isSingleDownloadOnly,
    })

  const getRowLabel = (
    item: {display_name?: string | null},
    isDownload: boolean
  ) =>
    getSettingFileRowLabel({
      displayName: item.display_name,
      panelTitle: panelMeta.title,
      isDownload,
    })

  const renderRowLabel = (
    iconNameForRow: string,
    rowLabel: string,
    description?: string | null,
    showInfoTooltip = true
  ) => {
    const normalizedDescription = description?.trim() ?? ''
    const showInlineDescription =
      normalizedDescription.length > 0 &&
      normalizedDescription !== rowLabel &&
      !normalizedDescription.includes('<') &&
      normalizedDescription !== panelMeta.description

    return (
      <div className='setting-file-action-callout__row-label-wrap'>
        <div className='setting-file-action-callout__row-icon'>
          <KTIcon iconName={iconNameForRow} className='fs-2 text-primary' />
        </div>
        <div className='setting-file-action-callout__row-text'>
          <div className='setting-file-action-callout__row-label form-label mb-0'>
            {formatSettingDisplayText(rowLabel)}
            {showInfoTooltip && normalizedDescription ? (
              <>
                &nbsp;&nbsp;
                <InfoTooltip
                  message={normalizedDescription}
                  sourceChannelName={sourceChannelName}
                />
              </>
            ) : null}
          </div>
          {showInlineDescription ? (
            <div className='setting-file-action-callout__row-description fs-7 mb-0'>
              {formatSettingDisplayText(normalizedDescription)}
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  const renderPanelHeaderContent = (title: string, description: string, headerIconName: string) => (
    <>
      <div className='setting-file-action-callout__icon'>
        <KTIcon iconName={headerIconName} className='fs-2 text-primary' />
      </div>
      <div className='setting-file-action-callout__content'>
        {title ? (
          <div className='setting-file-action-callout__title fw-bold fs-6 mb-1'>
            {formatSettingDisplayText(title)}
          </div>
        ) : null}
        {description ? (
          <div
            className='setting-file-action-callout__description fs-7 mb-0'
            dangerouslySetInnerHTML={{
              __html: getSanitizedSettingNotesHtml({
                notes: description,
                retrivedPlatformData,
                mainStoreData,
                pricingEnabledFrom: sourceChannelName ?? '',
              }),
            }}
          />
        ) : null}
      </div>
    </>
  )

  const getActionKey = (prefix: 'mapping' | 'child', id?: number | null) =>
    `${prefix}-${id ?? 'unknown'}`

  const handleDownloadMapping = async (mapping: FIELD_MAPPING_TYPE) => {
    const params = buildManageSettingFileParamsFromMapping(
      mapping,
      effectivePlatformId,
      parentAvailablePlatformSettingId
    )
    const actionKey = getActionKey('mapping', mapping.id ?? mapping.setting_mapping_id)

    if (!params) {
      errorToast('Missing file setting identifiers.')
      return
    }

    setDownloadingKey(actionKey)

    try {
      await downloadManageSettingFile(params)
    } catch (error) {
      const message = await extractManageSettingFileErrorMessage(error)
      errorToast(message)
    } finally {
      setDownloadingKey(null)
    }
  }

  const handleDownloadChild = async (child: SETTINGS_OF_PLATFORM) => {
    const params = buildManageSettingFileParamsFromChildSetting(child, effectivePlatformId)
    const actionKey = getActionKey('child', child.id)

    if (!params) {
      errorToast('Missing file setting identifiers.')
      return
    }

    setDownloadingKey(actionKey)

    try {
      await downloadManageSettingFile(params)
    } catch (error) {
      const message = await extractManageSettingFileErrorMessage(error)
      errorToast(message)
    } finally {
      setDownloadingKey(null)
    }
  }

  const openUploadPicker = (actionKey: string) => {
    setActiveUploadKey(actionKey)
    uploadInputRef.current?.click()
  }

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file || !activeUploadKey) return

    setUploadingKey(activeUploadKey)

    try {
      if (activeUploadKey.startsWith('mapping-') && uploadMapping) {
        const params = buildManageSettingFileParamsFromMapping(
          uploadMapping,
          effectivePlatformId,
          parentAvailablePlatformSettingId
        )

        if (!params) {
          errorToast('Missing file setting identifiers.')
          return
        }

        await uploadManageSettingFile(params, file)
      } else if (activeUploadKey.startsWith('child-') && uploadChild) {
        const params = buildManageSettingFileParamsFromChildSetting(
          uploadChild,
          effectivePlatformId
        )

        if (!params) {
          errorToast('Missing file setting identifiers.')
          return
        }

        await uploadManageSettingFile(params, file)
      }

      successToast('File uploaded successfully.')
    } catch (error) {
      const message = await extractManageSettingFileErrorMessage(error)
      errorToast(message)
    } finally {
      setUploadingKey(null)
      setActiveUploadKey(null)
    }
  }

  const renderDownloadButton = (
    buttonLabel: string,
    onClick: () => void,
    isLoading: boolean,
    variant: 'primary' | 'outline' = 'primary'
  ) => {
    const isOutline = variant === 'outline'

    return (
      <button
        type='button'
        className={`setting-file-action-callout__row-button setting-file-action-callout__download-btn btn btn-sm d-inline-flex align-items-center justify-content-center gap-2${
          isOutline
            ? ' setting-file-action-callout__download-btn--outline btn-light border'
            : ' btn-primary'
        }`}
        disabled={disabled || isLoading || downloadingKey !== null || uploadingKey !== null}
        onClick={onClick}
      >
        {isLoading ? (
          <Spinner animation='border' size='sm' />
        ) : (
          <KTIcon
            iconName='file-down'
            className={`fs-5 flex-shrink-0 ${isOutline ? 'text-dark' : 'text-white'}`}
          />
        )}
        <span className={isOutline ? 'text-dark' : 'text-white'}>
          {formatSettingDisplayText(buttonLabel)}
        </span>
      </button>
    )
  }

  const renderUploadButton = (buttonLabel: string, isLoading: boolean, actionKey: string) => (
    <button
      type='button'
      className='setting-file-action-callout__row-button setting-file-action-callout__upload-btn btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center gap-2'
      disabled={disabled || isLoading || downloadingKey !== null || uploadingKey !== null}
      onClick={() => openUploadPicker(actionKey)}
    >
      {isLoading ? (
        <Spinner animation='border' size='sm' />
      ) : (
        <KTIcon iconName='file-up' className='fs-5 text-white flex-shrink-0' />
      )}
      <span className='text-white'>{formatSettingDisplayText(buttonLabel)}</span>
    </button>
  )

  const renderActionRow = (sectionKey: string, rowContent: React.ReactNode) => (
    <div key={sectionKey} className='setting-file-action-callout__row'>
      {rowContent}
    </div>
  )

  const renderMappingSection = (mapping: FIELD_MAPPING_TYPE, isDownload: boolean) => {
    const actionKey = getActionKey('mapping', mapping.id ?? mapping.setting_mapping_id)
    const rowLabel = getRowLabel(mapping, isDownload)
    const buttonLabel = getButtonLabel(mapping, isDownload)
    const isLoading = isDownload
      ? downloadingKey === actionKey
      : uploadingKey === actionKey
    const showInfoTooltip = Boolean(
      mapping.description &&
        !mapping.description.includes('<') &&
        mapping.description.trim() !== rowLabel &&
        mapping.description.trim() !== panelMeta.description
    )

    return renderActionRow(
      actionKey,
      <>
        {renderRowLabel(
          isDownload ? getDownloadIconName() : getUploadIconName(),
          rowLabel,
          mapping.description,
          showInfoTooltip
        )}
        {isDownload
          ? renderDownloadButton(
              buttonLabel,
              () => handleDownloadMapping(mapping),
              isLoading
            )
          : renderUploadButton(buttonLabel, isLoading, actionKey)}
      </>
    )
  }

  const renderChildSection = (child: SETTINGS_OF_PLATFORM, isDownload: boolean) => {
    const actionKey = getActionKey('child', child.id)
    const rowLabel = getRowLabel(child, isDownload)
    const buttonLabel = getButtonLabel(child, isDownload)
    const isLoading = isDownload
      ? downloadingKey === actionKey
      : uploadingKey === actionKey
    const showInfoTooltip = Boolean(
      child.description &&
        !child.description.includes('<') &&
        child.description.trim() !== rowLabel &&
        child.description.trim() !== panelMeta.description
    )

    return renderActionRow(
      actionKey,
      <>
        {renderRowLabel(
          isDownload ? getDownloadIconName() : getUploadIconName(),
          rowLabel,
          child.description,
          showInfoTooltip
        )}
        {isDownload
          ? renderDownloadButton(
              buttonLabel,
              () => handleDownloadChild(child),
              isLoading
            )
          : renderUploadButton(buttonLabel, isLoading, actionKey)}
      </>
    )
  }

  if (!mappings.length && !childSettings.length) return null

  if (isSingleDownloadOnly) {
    const singleDownloadItem = downloadItem
    if (!singleDownloadItem) return null

    const actionKey = downloadMapping
      ? getActionKey('mapping', downloadMapping.id ?? downloadMapping.setting_mapping_id)
      : getActionKey('child', downloadChild?.id)
    const buttonLabel = getButtonLabel(singleDownloadItem, true)
    const title = panelMeta.title || singleDownloadItem.display_name
    const description = panelMeta.description || singleDownloadItem.description || ''
    const isLoading = downloadingKey === actionKey
    const onDownload = downloadMapping
      ? () => handleDownloadMapping(downloadMapping)
      : () => downloadChild && handleDownloadChild(downloadChild)

    return (
      <div className={`setting-file-action-callout-group ${className}`.trim()}>
        <div className='setting-file-action-callout'>
          <div className='setting-file-action-callout__header setting-file-action-callout__header--single'>
            {renderPanelHeaderContent(title, description, 'laptop')}
            {renderDownloadButton(buttonLabel, onDownload, isLoading)}
          </div>
        </div>
      </div>
    )
  }

  if (isInlineHeaderActions && downloadItem && uploadItem) {
    const downloadKey = downloadMapping
      ? getActionKey('mapping', downloadMapping.id ?? downloadMapping.setting_mapping_id)
      : getActionKey('child', downloadChild?.id)
    const uploadKey = uploadMapping
      ? getActionKey('mapping', uploadMapping.id ?? uploadMapping.setting_mapping_id)
      : getActionKey('child', uploadChild?.id)
    const downloadButtonLabel = getButtonLabel(downloadItem, true)
    const uploadButtonLabel = getButtonLabel(uploadItem, false)
    const isDownloadLoading = downloadingKey === downloadKey
    const isUploadLoading = uploadingKey === uploadKey
    const onDownload = downloadMapping
      ? () => handleDownloadMapping(downloadMapping)
      : () => downloadChild && handleDownloadChild(downloadChild)

    return (
      <div className={`setting-file-action-callout-group ${className}`.trim()}>
        <input
          ref={uploadInputRef}
          type='file'
          className='d-none'
          onChange={handleUpload}
          disabled={disabled}
        />

        <div className='setting-file-action-callout'>
          <div className='setting-file-action-callout__header setting-file-action-callout__header--inline-actions'>
            {renderPanelHeaderContent(panelMeta.title, panelMeta.description, iconName)}
            <div className='setting-file-action-callout__actions'>
              {renderDownloadButton(downloadButtonLabel, onDownload, isDownloadLoading, 'outline')}
              {renderUploadButton(uploadButtonLabel, isUploadLoading, uploadKey)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`setting-file-action-callout-group ${className}`.trim()}>
      <input
        ref={uploadInputRef}
        type='file'
        className='d-none'
        onChange={handleUpload}
        disabled={disabled}
      />

      <div className='setting-file-action-callout'>
        {showPanelHeader ? (
          <div className='setting-file-action-callout__header'>
            {renderPanelHeaderContent(panelMeta.title, panelMeta.description, iconName)}
          </div>
        ) : null}

        {downloadMapping ? renderMappingSection(downloadMapping, true) : null}
        {uploadMapping ? renderMappingSection(uploadMapping, false) : null}
        {downloadChild ? renderChildSection(downloadChild, true) : null}
        {uploadChild ? renderChildSection(uploadChild, false) : null}
      </div>
    </div>
  )
}

export default SettingFileActionCallout
